package wat.model

import kotlin.js.Date

data class Entry(
    val id: dynamic = null,
    val userId: String = "",
    val date: String = "",
    val weight: Double = 0.0,
    val sports: List<SportEntry> = emptyList(),
    val note: String = ""
)

data class SportEntry(
    val k: String = "",
    val mode: String = "",
    val `val`: Double = 0.0
)

data class Sport(
    val k: String,
    val i: String,     // icon emoji
    val l: String,     // name (Slovak - primary)
    val lEn: String = "",  // English name
    val lCz: String = "",  // Czech name
    val lDe: String = "",  // German name
    val u: String,     // default unit
    val cat: String,   // category key
    val dm: Int = 0,   // default duration
    val dv: Double = 0.0, // default value
    val met: Double = 3.0,
    val color: String = "",
    val isDefault: Boolean = false,
    val isCustom: Boolean = false,
    val activityId: dynamic = null,
    val availableUnits: List<String> = listOf("min")
) {
    /** Returns the localized name based on current language, falling back to Slovak */
    fun localName(): String {
        val name = when (wat.i18n.I18n.currentLang) {
            "en" -> lEn
            "cz" -> lCz
            "de" -> lDe
            else -> l
        }
        return name.ifBlank { l }
    }
}

data class Category(
    val id: dynamic,
    val key: String
)

data class MeasureUnit(
    val id: dynamic,
    val key: String,
    val step: Double = 1.0,
    val threshold: Double = 0.0,
    val stepAbove: Double = 0.0,
    val decimals: Int = 0
)

data class Profile(
    val height: Int = 0,
    val sex: String = "m",
    val goalWeight: Double = 0.0,
    val birthdate: String = "",
    val lang: String = "sk",
    val defaultPage: String = "train"
)

data class BmiZone(
    val minBmi: Double,
    val maxBmi: Double,
    val color: String,
    val translationKey: String
)

data class DiaryEntry(
    val id: dynamic = null,
    val userId: String = "",
    val date: String = "",
    val body: String = "",
    val mood: Int? = null,
    val tags: List<String> = emptyList()
)

data class SportDef(
    val on: Boolean = true,
    val mode: String = "",
    val min: Int = 0,
    val `val`: Double = 0.0,
    val gw: Double = 0.0
)

// Format helpers (String.format / "%.1f".format not available in Kotlin/JS)
fun fmt1(d: Double): String {
    val x = d.asDynamic().toFixed(1)
    return x as String
}

fun fmt0(d: Double): String {
    val x = d.asDynamic().toFixed(0)
    return x as String
}

// Helpers
fun todayLocal(): String {
    val d = Date()
    val y = d.getFullYear()
    val m = (d.getMonth() + 1).toString().padStart(2, '0')
    val day = d.getDate().toString().padStart(2, '0')
    return "$y-$m-$day"
}

fun formatDate(dateStr: String): String {
    val parts = dateStr.split("-")
    if (parts.size != 3) return dateStr
    return "${parts[2].toInt()}.${parts[1].toInt()}."
}

fun formatDateFull(dateStr: String): String {
    val parts = dateStr.split("-")
    if (parts.size != 3) return dateStr
    return "${parts[2].toInt()}. ${parts[1].toInt()}. ${parts[0]}"
}

fun calcAge(bd: String): Int {
    if (bd.isBlank()) return 0
    val parts = bd.split("-")
    if (parts.size != 3) return 0
    val now = Date()
    val bYear = parts[0].toIntOrNull() ?: return 0
    val bMonth = (parts[1].toIntOrNull() ?: return 0) - 1
    val bDay = parts[2].toIntOrNull() ?: return 0
    var age = now.getFullYear() - bYear
    if (now.getMonth() < bMonth || (now.getMonth() == bMonth && now.getDate() < bDay)) age--
    return maxOf(0, age)
}

fun daysBetween(a: String, b: String): Int {
    val da = Date(a).getTime()
    val db = Date(b).getTime()
    return ((db - da) / 86400000.0).toInt()
}
